import { H2 } from "@blueprintjs/core";
import { setCurrentRoute, setPreviousRoute } from "controllers/common/action";
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { constructPath, findRoute, routes } from "routes";
import { Analytics } from "utils/analytics";
import route from "./route";

export default function Platform() {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(setCurrentRoute(route));
    Analytics.getInstance().pageview(constructPath(findRoute(routes, route.id)));
    return () => dispatch(setPreviousRoute(route));
  });
  return (
    <section id="platform">
      <H2>{route.label}</H2>
      <p>
        Energy Storage Evaluation Tool (ESET) is based on a modular structure, as illustrated in the Figure below. One
        main advantage of such a structure is that different modules and components can be implemented in a separated
        and isolated manner, facilitating maintenance and expansion. In addition, data and applications are completely
        separated in ESET to improve data security.
      </p>
      <figure>
        <img src="products/ESET_architecutre.png" alt="" />
        <figcaption>ESET platform architecture</figcaption>
      </figure>
      <p>The platform consists of three modules, which are described as follows.</p>
      <ul>
        <li>
          <strong>Modeling &amp; Optimization Engines</strong> – This module provides an environment and process manager
          for simulation and optimization involved in ESS evaluation and sizing analyses. The analysis in each project
          is managed as a task– a process that includes I/O operations, optimization and simulation engine, pre- and
          post-processing, and financial analysis, etc. For each task, the input and log pipes are used to specify
          required inputs and query progress, respectively. Once a task is completed, the process manager forwards the
          outputs to the Information Exchange Portal. A request queue is set up to limit the number of tasks, and
          thereby avoiding running out of computing resources.
        </li>
        <li>
          <strong>Information Exchange Portal</strong> – This module serves as an information hub and interacts with
          users through http requests. Specifically, users access predefined Uniform Resource Locators (URLs) with
          standard http requests through either web browsers or command-line tools. A dynamic graphical user interface
          is developed to provide an interactive user experience for easy configuration and settings. An http request is
          then interpreted by the Python Flask Framework to either provide input data or query for information on
          projects. This module also interacts with the module of Modeling &amp; Optimization Engines through standard
          application programming interfaces (APIs), and interacts with the Database module through Structured Query
          Language (SQL) interfaces. The input data is forwarded to Modeling &amp; Optimization Engines to create a
          task. The query request is sent to Database to obtain the required data. Once the data is received, the module
          processes data and output results as tables and figures. The processed data, tables, and figures are then
          embedded into html contents using predefined templates. The html contents are then sent back to users and
          displayed in a web browser.
        </li>
        <li>
          <strong>Database</strong> – This module stores user information, user groups, permission, and inputs and
          outputs of each project using multiple tables. A dedicated table is designed to store user information. The
          group and permission tables are created and linked to user information for user management. In addition, each
          application has its own table. All the tables contain a field named <em>User_ID</em>, and each user has a
          unique <em>User_ID</em>. Through the <em>User_ID</em>, project data can be linked to users. For any tables
          that contain project data, there are two fields <em>Project_name</em> and <em>Project_status</em>. For each
          user, each project has a unique <em>Project_name</em>.
        </li>
      </ul>
      <figure>
        <img src="products/ESET_database.png" alt="" />
        <figcaption>Database architecture</figcaption>
      </figure>
      <p>
        To facilitate the deployment, an encapsulated environment with Docker is adopted to eliminate the need for
        customized settings. Docker is a set of platform as a service (PaaS) products that uses OS-level visualization
        to deliver software in packages called containers. Containers are isolated from one another and bundle their own
        software, libraries, and configuration files, and communicate with each other through a predefined TCP port and
        HTTP protocol within the network created through the Docker environment. A dedicated container is created for
        each component in ESET, as illustrated in the Figure below. We also set up a port forwarding between the
        Information Exchange Portal docker and the host operation system to allow ESET to take inputs outside the Docker
        environment. The platform and Docker containers can be deployed on a server or a local machine.
      </p>
      <figure>
        <img src="products/ESET_docker.png" alt="" />
        <figcaption>Docker-based deployment</figcaption>
      </figure>
      <p>
        A user account is required to use ESET. To use the ESET hosted at the PNNL server, users can click the
        <code>Create Account</code> button on the login page to register an account. Users’ information can be entered
        on the registration page, as shown in the Figure below. Once submitted, ESET sends an email that contains a link
        for users to confirm the registration request. The <code>Reset Password</code> button can be used when a user
        forgot the password. When ESET is deployed on a local machine, a predefined user credential (username:
        <em>test1</em>, password: <em>test1</em>) can be used. Users can modify account information and change password
        through account management, as shown in the Figure below.
      </p>
      <figure>
        <img src="products/ESET_create_account.png" alt="" />
        <figcaption>Create an account</figcaption>
      </figure>
      <figure>
        <img src="products/ESET_account_password.png" alt="" />
        <figcaption>Account management</figcaption>
      </figure>
      <p>
        Once logged in to the ESET platform, a user is navigated to the dashboard page, as shown in the Figure below.
      </p>
      <figure>
        <img src="products/ESET_dashboard.png" alt="" />
        <figcaption>User dashboard</figcaption>
      </figure>
      <p>The dashboard page consists of three parts: module list, menu, and project list.</p>
      <ul>
        <li>
          {" "}
          In the module list area, users can click a module to create a new project. A pop-up window is displayed for
          users to enter a project name. Note that the project name should be unique under a user’s account.{" "}
        </li>
        <li>
          {" "}
          In the menu area, the user ID is displayed, followed by two buttons: <code>Account</code> and
          <code>Logout</code>. By clicking the <code>Account</code> button, a user is navigated to a new web page where
          they can review and edit the account information. Clicking the <code>Logout</code> button returns to the
          login/registration page.{" "}
        </li>
        <li>
          {" "}
          All the existing projects under a user’s account are displayed in the project list area. There are four
          fields: Model Name, Type, Created Time, and Modified Time. Users can sort projects by field by clicking a
          field name. Users can delete, rename, and duplicate a project using the three buttons below “Options.” By
          click a project’s model name, users are navigated to the project input page, where they can view and edit a
          project.{" "}
        </li>
      </ul>
      <p>
        The project input page consists of five parts: input/output navigation, menu, project setting and parameters,
        buttons for actions, and optimization log, as shown in the Figure below.{" "}
      </p>
      <figure>
        <img src="products/ESET_input.png" alt="" />
        <figcaption>Project input page</figcaption>
      </figure>
      <ul>
        <li>
          {" "}
          The input/output navigation allows users to switch between the two web pages. Note that the
          <code>Output</code> button is disabled for a project without analysis results (e.g., a new project) or when a
          project is running.{" "}
        </li>
        <li>
          {" "}
          There are two buttons in the menu area: <code>Dashboard</code> and <code>Logout</code>. The
          <code>Dashboard</code> button is used to return to the dashboard page.{" "}
        </li>
        <li>
          {" "}
          In the area of action buttons, the <code>Save</code> button is used to save project inputs into the database.
          The <code>Run</code> button is used to start the evaluation. The <code>Cancel</code> button is used to
          terminate the evaluation, which is disabled unless a project is running.{" "}
        </li>
        <li>
          {" "}
          In the setting and parameter area, users can change settings and parameters and upload customized input files.
          Help text is displayed to provide additional information when moving cursor over a setting or parameter name,
          as shown in the Figure below. When a series of data are required as inputs, templates are developed for users
          to prepare and upload inputs. The <code>Default</code> button is used to reset an input to the default file.
          Users can download the current input file using the <code>Download</code> button to view or edit the content,
          and replace an existing input file with a new customized one using the
          <code>Upload</code> button.{" "}
        </li>
        <li> In the log area, optimization progress information is displayed. </li>
      </ul>
      <figure>
        <img src="products/ESET_helptext.png" alt="" />
        <figcaption>Help text</figcaption>
      </figure>
      <p>
        Once the analysis is completed, a pop-up window is displayed, as shown in figure below. Clicking the
        <code>OK</code> button, the link to the <code>Output</code> page in the input/output navigation area is enabled.
      </p>
      <figure>
        <img src="products/ESET_project_finished.png" alt="" />
        <figcaption>Notification message when an evaluation is completed</figcaption>
      </figure>
      <p>
        An example output page is shown in figure below, where a summary of results and visualization of different
        results are available. ESET currently supports four types of figures: 3-D surface chart, pie chart, bar chart,
        and time series chart. There is a <code>Download</code> button in all the figures for users to download the
        numerical data for a record or customized analysis. Detailed information is displayed when users move the cursor
        to a legend, a slice in a pie chart, a bar in a bar chart, and a point in a 3-D surface and time series chart.
        By clicking a legend, line, or pie slice, users can show or hide the corresponding line or pie slice. For 3-D
        surface charts, users can view graphs from different angles. For time series charts, users can select a
        particular area to zoom in and explore the results in detail and click the <code>Reset Zoom</code> button to
        exit a zoom-in mode and return to the original axes limits.
      </p>
      <figure>
        <img src="products/ESET_output.png" alt="" />
        <figcaption>Project output page</figcaption>
      </figure>
    </section>
  );
}
